import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  CircularProgress,
  Chip,
  Typography,
  InputAdornment,
  Box,
  AppBar,
  Toolbar,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import LoadingBar from 'react-top-loading-bar';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const TeacherAllTopics = () => {
  const navigate = useNavigate();
  const drawerWidth = 240;

  // State for dropdowns
  const [teacherId, setTeacherId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');

  // State for dropdown data
  const [teacherData, setTeacherData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);

  // State for topics and UI
  const [topics, setTopics] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showModal, setShowModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState({ topicId: null, topicName: '' });
  const [canDelete, setCanDelete] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [canAdd, setCanAdd] = useState(true);

  // Fetch Teacher Data
  const fetchTeacherData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetTeacher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        setTeacherData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch teacher data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setTeacherData([]);
    }
  };

  // Fetch Class Data
  const fetchClassData = async (selectedTeacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetClass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ teacherId: selectedTeacherId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setClassData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch class data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setClassData([]);
    }
  };

  // Fetch Section Data
  const fetchSectionData = async (selectedClassId, selectedTeacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/ddlSection_clsId`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, teacherId: selectedTeacherId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setSectionData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch section data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setSectionData([]);
    }
  };

  // Fetch Subject Data
  const fetchSubjectData = async (selectedClassId, selectedTeacherId, selectedSectionId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/Subjectddl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, teacherId: selectedTeacherId, sectionId: selectedSectionId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setSubjectData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch subject data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setSubjectData([]);
    }
  };

  // Fetch Topics Data
  const fetchTopicsData = async (selectedClassId, selectedSectionId, selectedSubjectId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/TeachingTopics/GetByClassSubject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, sectionId: selectedSectionId, subjectId: selectedSubjectId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setTopics(responseData.topics || []);
        setFilteredData(responseData.topics || []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch topics');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Fetch Teacher Data on Component Mount
  useEffect(() => {
    fetchTeacherData();
  }, []);

  // Fetch Class Data when Teacher is Selected
  useEffect(() => {
    if (teacherId) {
      fetchClassData(teacherId);
      setClassId('');
      setSectionId('');
      setSubjectId('');
    }
  }, [teacherId]);

  // Fetch Section Data when Class is Selected
  useEffect(() => {
    if (classId && teacherId) {
      fetchSectionData(classId, teacherId);
      setSectionId('');
      setSubjectId('');
    }
  }, [classId, teacherId]);

  // Fetch Subject Data when Section is Selected
  useEffect(() => {
    if (sectionId && classId && teacherId) {
      fetchSubjectData(classId, teacherId, sectionId);
      setSubjectId('');
    }
  }, [sectionId, classId, teacherId]);

  // Fetch Topics when Subject is Selected
  useEffect(() => {
    if (subjectId && sectionId && classId) {
      fetchTopicsData(classId, sectionId, subjectId);
    }
  }, [subjectId, sectionId, classId]);

  // Search Logic
  useEffect(() => {
  const filtered = topics.filter((topic) =>
    topic.topicName
      ?.toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
  );

  setFilteredData(filtered);
  setPage(1);
}, [searchTerm, topics]);


  // Pagination Logic
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Modal Logic
  const handleShow = (topicId, topicName) => {
    setSelectedTopic({ topicId, topicName });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleDelete = async () => {
  try {
    setLoadingBarProgress(30);
    const token = sessionStorage.getItem('token');
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/TeachingTopics/Delete/${selectedTopic.topicId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      }
    );

    const responseData = await response.json();

    if (response.ok) {
      setFilteredData(filteredData.filter((topic) => topic.topicId !== selectedTopic.topicId));
      setShowModal(false);
      setLoadingBarProgress(100);
      setSnackbarMessage(responseData.msg || 'Topic deleted successfully!');
      setSnackbarSeverity(responseData.msg.includes('failed') ? 'error' : 'success');
      setOpenSnackbar(true);
    } else {
      setSnackbarMessage(responseData.msg || 'Failed to delete topic');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setLoadingBarProgress(0);
    }
  } catch (error) {
    setSnackbarMessage('An error occurred. Please try again later.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    setLoadingBarProgress(0);
  }
};


  const handleEdit = (topicId) => {
    navigate(`/TeacherTracker/AllTopics/edit-topic/${topicId}`);
  };

  const handleAdd = () => {
    navigate('/TeacherTracker/AllTopics/add-topic');
  };

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: '64px' },
        p: 3,
        backgroundColor: '#f4f6f9',
        minHeight: '100vh',
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
        backgroundColor: '#d97706',
          mb: 1
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 600, letterSpacing: '0.5px' }}>
            All Topics
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper
        elevation={3}
        sx={{
          padding: 3,
          width: '100%',
          margin: 'auto',
        //   borderRadius: '0 0 8px 8px',
          backgroundColor: '#fafafa'
        }}
      >
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />

        {/* Search and Add Topic Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
        <TextField
  variant="outlined"
  placeholder="Search topics..."
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  sx={{
    width: { xs: '100%', sm: '350px' },
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '&:hover fieldset': {
        borderColor: '#0B1F3D',
      },
    },
  }}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <SearchIcon sx={{ color: '#666' }} />
      </InputAdornment>
    ),
  }}
/>
  
           
          <Tooltip title={!canAdd ? 'You are not authorized to add' : ''} arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={!canAdd}
                color='primary'
                sx={{
                  width: { xs: '100%', sm: '140px' },
                   backgroundColor: '#d97706', 
                  height: '56px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  letterSpacing: '0.5px',
                   '&:hover': {
      backgroundColor: '#c2410c', // darker orange hover
    },
                }}
              >
                ADD TOPIC
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Dropdowns for Teacher, Class, Section, Subject */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2.5,
            mb: 3,
            p: 2,
            backgroundColor: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="teacher">Teacher</InputLabel>
            <Select
              id="teacher"
              value={teacherId}
              label="Teacher"
              onChange={(e) => setTeacherId(e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            >
              {teacherData.map((teacher) => (
                <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                  {teacher.employeeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="class">Class</InputLabel>
            <Select
              id="class"
              value={classId}
              label="Class"
              onChange={(e) => setClassId(e.target.value)}
              disabled={!teacherId}
              sx={{ backgroundColor: '#fff' }}
            >
              {classData.map((classItem) => (
                <MenuItem key={classItem.classId} value={classItem.classId}>
                  {classItem.className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="section">Section</InputLabel>
            <Select
              id="section"
              value={sectionId}
              label="Section"
              onChange={(e) => setSectionId(e.target.value)}
              disabled={!classId}
              sx={{ backgroundColor: '#fff' }}
            >
              {sectionData.map((sectionItem) => (
                <MenuItem key={sectionItem.sectionId} value={sectionItem.sectionId}>
                  {sectionItem.sectionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="subject">Subject</InputLabel>
            <Select
              id="subject"
              value={subjectId}
              label="Subject"
              onChange={(e) => setSubjectId(e.target.value)}
              disabled={!sectionId}
              sx={{ backgroundColor: '#fff' }}
            >
              {subjectData.map((subjectItem) => (
                <MenuItem key={subjectItem.subjectId} value={subjectItem.subjectId}>
                  {subjectItem.subjectName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Topics Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <Table
            sx={{
              minWidth: 650,
              '& .MuiTableCell-root': {
                borderRight: '1px solid #e0e0e0',
                fontSize: '0.95rem',
                padding: '14px 16px',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontWeight: 700,
                backgroundColor: '#f8f9fa',
                color: '#2c3e50',
                borderBottom: '2px solid #dee2e6',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
              },
              '& .MuiTableBody-root .MuiTableRow-root': {
                '&:hover': {
                  backgroundColor: '#f5f7fa',
                  transition: 'background-color 0.2s ease',
                },
                '&:last-child td': {
                  borderBottom: 'none',
                }
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Topic Order</TableCell>
                <TableCell>Topic Name</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Covered By</TableCell>
                <TableCell>Date Covered</TableCell>
                <TableCell sx={{ textAlign: 'center', borderRight: 'none !important' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingBarProgress > 0 && loadingBarProgress < 100 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, borderRight: 'none !important' }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
                      Loading topics...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((topic, index) => (
                  <TableRow
                    key={topic.topicId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {topic.topicOrder}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                        {topic.topicName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.subjectName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.className}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {topic.status === 'P' ? (
                        <Chip
                          label="Pending"
                          size="small"
                          sx={{
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                      ) : topic.status === 'C' ? (
                        <Chip
                          label="Completed"
                          size="small"
                          sx={{
                            backgroundColor: '#d4edda',
                            color: '#155724',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                      ) : (
                        <Typography variant="body2">{topic.status}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.coveredByTeacherName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#495057' }}>
                        {topic.dateCovered || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', borderRight: 'none !important' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title={canEdit ? '' : 'You are not authorized to edit'} arrow>
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              color='warning'
                              onClick={() => handleEdit(topic.topicId)}
                              disabled={!canEdit}
                              startIcon={<EditIcon sx={{ fontSize: '1rem' }} />}
                              sx={{
                                minWidth: '85px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                padding: '6px 12px',
                              }}
                            >
                              Edit
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
  title={
    !canDelete
      ? 'You are not authorized to delete'
      : topic.status === 'C'
      ? 'Completed topic cannot be deleted'
      : ''
  }
  arrow
>
                          <span>
                        <Button
  variant="contained"
  size="small"
  color="error"
  onClick={() => handleShow(topic.topicId, topic.topicName)}
  disabled={!canDelete || topic.status === 'C'}
  startIcon={<DeleteIcon sx={{ fontSize: '1rem' }} />}
  sx={{
    minWidth: '95px',
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '6px 12px',
  }}
>
  Delete
</Button>

                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, borderRight: 'none !important' }}>
                    <Typography variant="body1" sx={{ color: '#999', fontWeight: 500 }}>
                      No Topics Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#bbb', mt: 1 }}>
                      Please select filters to view topics
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredData.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
              p: 2,
              backgroundColor: '#ffffff',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 2
            }}
          >
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{
                width: { xs: '100%', sm: 150 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              SelectProps={{ native: true }}
            >
              {[5, 10, 25, 50].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#495057'
                }}
              >
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 1}
                  sx={{
                    minWidth: '80px',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
                  sx={{
                    minWidth: '80px',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Delete Confirmation Modal */}
        <Modal open={showModal} onClose={handleClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#0B1F40',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}
              >
                Delete Confirmation
              </Typography>
              <Typography sx={{ color: '#ffd700', fontSize: '1.3rem' }}>⚠️</Typography>
            </Box>
            <Box sx={{ padding: '24px 20px' }}>
              <Typography
                sx={{
                  mb: 3,
                  fontSize: '1rem',
                  color: '#495057',
                  lineHeight: 1.6
                }}
              >
                Are you sure you want to delete{' '}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D'
                  }}
                >
                  {selectedTopic.topicName}
                </Typography>
                ?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#dc3545',
                  mb: 3,
                  fontSize: '0.9rem'
                }}
              >
                This action cannot be undone.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  sx={{
                    borderColor: '#6c757d',
                    color: '#6c757d',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    '&:hover': {
                      borderColor: '#5a6268',
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleDelete}
                  sx={{
                    backgroundColor: '#dc3545',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    '&:hover': {
                      backgroundColor: '#c82333',
                    }
                  }}
                >
                  Yes, Delete
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              borderRadius: 2,
              fontWeight: 500
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default TeacherAllTopics;
